import React, { useState, useMemo, useCallback } from "react";
import { getAffiliationColor, getOverride, getRaceEevpSentence } from "../helpers";
import WidgetName from "../WidgetName";
import { georgiaCountyPaths, georgiaCountyBubbles, MAP_VIEWBOX_WIDTH, MAP_VIEWBOX_HEIGHT } from "./georgia-counties";
import { normalizeCountyKey, getCountyDisplayName } from "./countyNames";
import "./results-map.scss";
import ErrorBoundary from "../ErrorBoundary";
import { getRaceTitle, findRaceCandidate, applyDisplayOverrides, applyStatewideOverrides } from "./overrides";

// Mostly self-contained on purpose (mirrors normalizeRU/buildCountyResults/getStateResult/partyLabel/
// MapLegend from ResultsMapWidget.js) — this is a separate, independently embeddable widget type,
// so it doesn't import from that file. The one shared piece is ./countyNames (county key and
// display-name helpers), so both map widgets join and label counties identically.

const normalizeRU = (ru, race) => {
  const countyName = normalizeCountyKey(ru.countyName || ru.reportingunitName);
  if (!countyName) return null;

  const candidates = (ru.candidates || []).map((c) => ({
    ...c,
    name: c.name || [c.first || c.firstName, c.last || c.lastName].filter(Boolean).join(" "),
    votes: c.votes ?? c.voteCount ?? 0,
    percentage: c.percentage ?? 0,
    party: c.party || "",
    winner: getOverride(c, "winner") === true,
  })).map((c) => applyDisplayOverrides(c, findRaceCandidate(race, c))); // county rows have no overrides; only name/party follow the race-level candidate

  const totalVotes = candidates.reduce((s, c) => s + c.votes, 0);
  const withPct = candidates.map((c) => ({
    ...c,
    percentage: c.percentage || (totalVotes > 0 ? parseFloat((c.votes / totalVotes * 100).toFixed(1)) : 0),
  }));

  const sorted = [...withPct].sort((a, b) => b.votes - a.votes);
  const leader = sorted[0];
  if (!leader || leader.votes === 0) return null;

  return {
    countyName,
    party: leader.party,
    allCandidates: sorted,
    precinctsReporting: ru.precinctsReporting ?? null,
    precinctsTotal: ru.precinctsTotal ?? null,
  };
};

const buildCountyResults = (races) => {
  const countyMap = {};
  races.forEach((race) => {
    const rus = race.reportingUnits || [];
    const countyRUs = rus.filter((ru) =>
      ru.countyName || ru.level === "subunit" || ru.reportingunitLevel === 2
    );
    countyRUs.forEach((ru) => {
      const normalized = normalizeRU(ru, race);
      if (!normalized || countyMap[normalized.countyName]) return;
      countyMap[normalized.countyName] = normalized;
    });
  });
  return countyMap;
};

/** County EEVP sentences keyed like countyResults (normalized county name), from each county reporting unit's own `eevp`.
 * Separate from countyResults because normalizeRU drops counties with no votes yet, which still carry a real EEVP (e.g. 0). */
const buildCountyEevpSentences = (races) => {
  const map = {};
  races.forEach((race) => {
    (race.reportingUnits || [])
      .filter((ru) => ru.countyName || ru.level === "subunit" || ru.reportingunitLevel === 2)
      .forEach((ru) => {
        const key = normalizeCountyKey(ru.countyName || ru.reportingunitName);
        const sentence = getRaceEevpSentence(ru);
        if (key && sentence && !map[key]) map[key] = sentence;
      });
  });
  return map;
};

const getStateResult = (races) => {
  for (const race of races) {
    const rus = race.reportingUnits || [];
    const stateRU = rus.find((ru) =>
      ru.level === "state" || ru.reportingunitLevel === 1 ||
      (!ru.countyName && !ru.reportingunitName && ru.candidates?.length)
    );
    const src = stateRU || (race.candidates?.length ? race : null);
    if (!src) continue;
    const rawCandidates = stateRU ? stateRU.candidates : race.candidates;
    const candidates = (rawCandidates || []).map((c) => ({
      ...c,
      name: c.name || [c.first || c.firstName, c.last || c.lastName].filter(Boolean).join(" "),
      votes: c.votes ?? c.voteCount ?? 0,
      percentage: c.percentage ?? 0,
      winner: getOverride(c, "winner") === true,
    })).map((c) => applyStatewideOverrides(c, findRaceCandidate(race, c)));
    const sorted = [...candidates].sort((a, b) => b.votes - a.votes);
    if (sorted[0] && sorted[0].votes > 0) {
      return {
        raceName: getRaceTitle(race),
        // Overall race-level sentence from race.eevp, or null (then nothing is shown); same race as picked here.
        eevpSentence: getRaceEevpSentence(race),
        allCandidates: sorted,
      };
    }
  }
  return null;
};

const PARTY_LEGEND_ITEMS = [
  { label: "Rep", color: getAffiliationColor("rep") },
  { label: "Dem", color: getAffiliationColor("dem") },
  { label: "Lib", color: getAffiliationColor("lib") },
  { label: "Grn", color: getAffiliationColor("grn") },
  { label: "Oth", color: getAffiliationColor("oth") },
  { label: "No Data", color: "#d0d0d0" },
];

/** SVG legend rendered inside the map SVG — a row of party-colored dots + labels (colors from
 * getAffiliationColor, same source of truth as the map/panel), plus the reporting-count text on
 * its own line above (it's ~30 characters — sharing the legend's row would overlap the items). */
const PartyLegend = ({ reportingCount, totalCounties }) => {
  const itemWidth = 66;

  return (
    <g transform="translate(30, 515)">
      {totalCounties > 0 && (
        <text x="420" y="-18" textAnchor="end" style={{ fontFamily: "var(--ec-font-sans)", fontSize: 11, fontWeight: 500, fill: "var(--ec-ink-3)" }}>
          {reportingCount} of {totalCounties} counties reporting
        </text>
      )}
      {PARTY_LEGEND_ITEMS.map((item, i) => (
        <g key={item.label} transform={`translate(${i * itemWidth}, 0)`}>
          <circle cx="5" cy="-4" r="5" fill={item.color} />
          <text x="15" y="0" style={{ fontFamily: "var(--ec-font-sans)", fontSize: 11, fill: "var(--ec-ink-2)" }}>{item.label}</text>
        </g>
      ))}
    </g>
  );
};

/** Choropleth path, hover-only (no click/selection — this widget has no table/detail card to link to) */
const OverlayCountyPath = ({ county, countyResults, onHover }) => {
  const [hovered, setHovered] = useState(false);
  const normalizedName = normalizeCountyKey(county.name);
  const result = countyResults[normalizedName];

  const fill = result ? getAffiliationColor(result.party) : "#d0d0d0";
  // Flat 0.35 — matches map.html's actual baseline fill-opacity for county shapes exactly.
  const fillOpacity = 0.35;
  const stroke = hovered ? "#666666" : "#CCCCCC";
  const strokeWidth = hovered ? 1.5 : 0.5;

  return (
    <path
      d={county.path}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={stroke}
      strokeWidth={strokeWidth}
      style={{ cursor: result ? "pointer" : "default", transition: "stroke-width 0.15s" }}
      onMouseEnter={() => { setHovered(true); onHover(county.name, result); }}
      onMouseLeave={() => { setHovered(false); onHover(null, null); }}
    />
  );
};

const MAX_BUBBLE_R = 20;

/** Bubble sized by the county's total vote weight, hover-only (no click/selection) */
const OverlayCountyBubble = ({ bubble, countyResults, maxVotes, onHover }) => {
  const [hovered, setHovered] = useState(false);
  const normalizedName = normalizeCountyKey(bubble.name);
  const result = countyResults[normalizedName];

  const totalVotes = result ? result.allCandidates.reduce((s, c) => s + c.votes, 0) : 0;
  const r = maxVotes > 0 && totalVotes > 0
    ? Math.max(4, Math.sqrt(totalVotes / maxVotes) * MAX_BUBBLE_R)
    : 4;

  const fill = result ? getAffiliationColor(result.party) : "#d0d0d0";
  const stroke = hovered ? "#555555" : "#FFFFFF";
  const strokeWidth = hovered ? 1.5 : 0.5;

  return (
    <circle
      cx={bubble.cx}
      cy={bubble.cy}
      r={r}
      fill={fill}
      fillOpacity={1}
      stroke={stroke}
      strokeWidth={strokeWidth}
      style={{ cursor: result ? "pointer" : "default", transition: "r 0.15s, stroke-width 0.15s" }}
      onMouseEnter={() => { setHovered(true); onHover(bubble.name, result); }}
      onMouseLeave={() => { setHovered(false); onHover(null, null); }}
    />
  );
};

const ResultsMapOverlayWidget = (props) => {
  const races = useMemo(() => props.races || [], [props.races]);
  const [hoveredCounty, setHoveredCounty] = useState(null);

  const handleHover = useCallback((name, result) => {
    setHoveredCounty(name ? { name, result } : null);
  }, []);

  const rawStateResult = useMemo(() => getStateResult(races), [races]);
  const countyResults = useMemo(() => buildCountyResults(races), [races]);
  const countyEevp = useMemo(() => buildCountyEevpSentences(races), [races]);

  // The statewide sentence comes only from the race eevp; it is never aggregated
  // from county counts. A null sentence hides it.
  const stateResult = rawStateResult;

  const reportingCount = useMemo(() => {
    return Object.values(countyResults).filter(
      (r) => (r.precinctsReporting ?? 0) > 0
    ).length;
  }, [countyResults]);

  const maxVotes = useMemo(() => {
    return Object.values(countyResults).reduce((max, r) => {
      const total = r.allCandidates.reduce((s, c) => s + c.votes, 0);
      return total > max ? total : max;
    }, 0);
  }, [countyResults]);

  const panelCandidates = hoveredCounty?.result
    ? hoveredCounty.result.allCandidates.filter((c) => c.votes > 0)
    : (stateResult?.allCandidates.filter((c) => c.votes > 0) || []);
  const panelTotalVotes = panelCandidates.reduce((s, c) => s + c.votes, 0);

  const panelTitle = hoveredCounty
    ? `${getCountyDisplayName(hoveredCounty.name)} County`
    : (stateResult?.raceName || "Statewide Results");

  // Hovering a county shows only that county's own sentence (never the statewide one); none if it has no eevp.
  const panelEevpSentence = hoveredCounty
    ? (countyEevp[normalizeCountyKey(hoveredCounty.name)] || null)
    : (stateResult?.eevpSentence || null);

  const panelSubtitle = hoveredCounty
    ? "County results"
    : "Statewide";

  return (
    <div className="ec-dashboard" data-theme="light">
      <div className="ec-content">
        <WidgetName widget={props.widget} updated={props.updated} />

        <div className="ec-map-row">
          <div className="ec-map-col">
            <div className="rm-map-frame">
              <svg
                viewBox={`0 0 ${MAP_VIEWBOX_WIDTH} ${MAP_VIEWBOX_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                className="rm-map-svg"
                role="img"
                aria-label="Georgia county results map"
              >
                <g className="county-boundaries">
                  {georgiaCountyPaths.map((county, i) => (
                    <ErrorBoundary key={i} message="">
                      <OverlayCountyPath county={county} countyResults={countyResults} onHover={handleHover} />
                    </ErrorBoundary>
                  ))}
                </g>
                <g>
                  {georgiaCountyBubbles.map((bubble, i) => (
                    <ErrorBoundary key={i} message="">
                      <OverlayCountyBubble bubble={bubble} countyResults={countyResults} maxVotes={maxVotes} onHover={handleHover} />
                    </ErrorBoundary>
                  ))}
                </g>
                <PartyLegend reportingCount={reportingCount} totalCounties={georgiaCountyPaths.length} />
              </svg>
            </div>
          </div>

          <div className="ec-table-col ec-card rm-overlay-panel">
            <div className="ec-card-header">
              <div>
                <div className="ec-card-title">{panelTitle}</div>
                <div className="ec-card-subtitle">{panelSubtitle}</div>
                {panelEevpSentence && <div className="ec-card-eevp">{panelEevpSentence}</div>}
              </div>
            </div>
            <div className="rm-overlay-body">
              {panelTotalVotes > 0 && (
                <div className="rm-overlay-total">{panelTotalVotes.toLocaleString()} Total Votes</div>
              )}
              {panelCandidates.map((c, i) => {
                const color = getAffiliationColor(c.party);
                return (
                  <div key={i} className="rm-overlay-candidate">
                    <div className="rm-overlay-candidate-top">
                      <span className="ec-party-dot" style={{ background: color }} />
                      <span className="rm-overlay-cand-name">{c.name}</span>
                      <span className="rm-overlay-cand-pct" style={{ color }}>
                        {c.percentage}%{c.winner && <span className="rm-winner-check">✓</span>}
                      </span>
                    </div>
                    <div className="ec-bar-track">
                      <div className="ec-bar-fill" style={{ width: `${c.percentage}%`, background: color }} />
                    </div>
                    <div className="rm-overlay-cand-votes">{c.votes.toLocaleString()} votes</div>
                  </div>
                );
              })}
              {!hoveredCounty && (
                <div className="rm-overlay-hint">Hover over a county to see county results</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsMapOverlayWidget;
